import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type { OptionsDocument } from 'paintscout'
import { TileList } from '@paintscout/ui'
import type { ProductOption } from '@paintscout/util/builder'
import { getProductOptions } from '@paintscout/util/builder'
import React from 'react'
import uniqBy from 'lodash/uniqBy'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface ProductSelectorProps extends WithStyles<typeof styles> {
  options?: OptionsDocument
  quoteTypes?: string[]
  query?: string
  canDeactivate?: boolean

  allowCustom?: boolean

  onChange?: (event: any, product: ProductOption, custom?: boolean) => void
}

export const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    rootEmpty: {
      paddingTop: theme.spacing(2)
    },
    formSection: {
      padding: '1.5rem 0'
    },
    form: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center'
    },
    thumbnailImg: {
      height: 50,
      objectFit: 'contain',
      marginRight: theme.spacing(1),
      [theme.breakpoints.down(500)]: {
        display: 'none'
      }
    },
    thumbnail: {
      width: 'auto',
      height: 50,
      objectFit: 'contain',
      [theme.breakpoints.down(500)]: {
        display: 'none'
      }
    },
    hasThumbnail: {
      alignItems: 'center'
    }
  })
}

class ProductSelector extends React.PureComponent<ProductSelectorProps> {
  public render() {
    const { classes, options, query, quoteTypes = [], canDeactivate } = this.props

    const productOptions = uniqBy(
      quoteTypes.reduce((acc, quoteType) => {
        const newOptions = getProductOptions({ options, quoteType, canDeactivate })
        return [...acc, ...newOptions]
      }, []),
      'key'
    )

    const searchQuery = query?.toLowerCase().trim() || ''
    const filteredOptions = searchQuery
      ? productOptions.filter((option: ProductOption) => option.label.toLowerCase().indexOf(searchQuery) !== -1)
      : productOptions

    const mappedOptions = filteredOptions.map((option: ProductOption) => ({
      ...option,
      sublabel: option.manufacturer || '',
      ...(option.featureImage?.value && {
        Thumbnail: () => <img className={classes.thumbnailImg} src={option.featureImage.value} alt={option.label} />
      })
    }))

    const hasNoProducts = mappedOptions.length === 0
    const noItemTitle =
      hasNoProducts && searchQuery ? 'No products found' : hasNoProducts ? 'No products available' : undefined
    const noItemHelpText =
      hasNoProducts && searchQuery
        ? `No products match "${query}". Try a different search term.`
        : hasNoProducts
          ? 'There are no products configured for the selected quote types.'
          : undefined

    return (
      <div className={`${classes.root} ${hasNoProducts ? classes.rootEmpty : ''}`}>
        <TileList
          variant="select"
          title="Product"
          tileClasses={{ thumbnail: classes.thumbnail, hasThumbnail: classes.hasThumbnail }}
          items={mappedOptions}
          onSelect={(ev, key: string) => this.handleChange(ev, key, false, productOptions)}
          onCustom={(ev) => this.handleChange(ev, null, true, productOptions)}
          subLabelField={'sublabel'}
          noItemTitle={noItemTitle}
          noItemHelpText={noItemHelpText}
        />
      </div>
    )
  }

  public handleChange = (event: any, key: string, custom: boolean, productOptions: ProductOption[]) => {
    this.props.onChange(
      event,
      productOptions.find((i) => i.key === key),
      custom
    )
  }
}

const StyledProductSelector = withStyles(styles)(ProductSelector)

function ProductSelectorWithFlags(props: Omit<ProductSelectorProps, 'canDeactivate' | 'classes'>) {
  const { canDeactivateSettingsItems } = useFlags()
  return <StyledProductSelector {...props} canDeactivate={!!canDeactivateSettingsItems} />
}

export default ProductSelectorWithFlags
